import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useSupportContext } from './SupportContext';
import { HQ_STAFF_ROLES } from '../utils/hostnamePortal';
import { isDevAuthToken } from '../utils/devAuth';
import {
  canCallAgencyStaffApis,
  homePathForScopedSession,
  isScopedSelfRepairSession,
  resolveSessionPortal,
  resolveSessionRole,
} from '../utils/sessionScope';
import { clearAccountScopedClientState } from '../utils/authUtils';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

/**
 * Portal Context - Phase 6
 * 
 * Provides portal-specific data and capabilities throughout the app.
 * Fetches from /api/portal/access/check to get role, portal_type, capabilities.
 */

const PortalContext = createContext(null);

export const usePortal = () => {
  const context = useContext(PortalContext);
  if (!context) {
    throw new Error('usePortal must be used within a PortalProvider');
  }
  return context;
};

// Portal type constants
export const PORTAL_TYPES = {
  STAFF: 'staff_portal',
  SELF_SERVE: 'self_serve_portal',
  MANAGED_CLIENT: 'managed_client_portal',
};

// Role to portal mapping (frontend fallback if backend doesn't respond)
const ROLE_TO_PORTAL = {
  platform_owner: PORTAL_TYPES.STAFF,
  platform_admin: PORTAL_TYPES.STAFF,
  super_admin: PORTAL_TYPES.STAFF,
  admin: PORTAL_TYPES.STAFF,
  owner: PORTAL_TYPES.STAFF,
  specialist: PORTAL_TYPES.STAFF,
  staff: PORTAL_TYPES.STAFF,
  agency_owner: PORTAL_TYPES.STAFF,
  agency_admin: PORTAL_TYPES.STAFF,
  agency_staff: PORTAL_TYPES.STAFF,
  agency_member: PORTAL_TYPES.STAFF,
  self_repair_customer: PORTAL_TYPES.SELF_SERVE,
  managed_client: PORTAL_TYPES.MANAGED_CLIENT,
};

const PORTAL_FETCH_COOLDOWN_MS = 15000;

const portalFetchTracker = {
  fetchKey: null,
  lastFetchAt: 0,
  inFlight: false,
};

const buildPortalFetchKey = ({
  userId,
  userRole,
  isInSupportMode,
  effectiveUserId,
  effectivePortalType,
  contextReady,
}) => {
  const actorId = userId || 'unknown';
  const effectiveId = isInSupportMode ? effectiveUserId || actorId : actorId;
  const portalScope = isInSupportMode ? effectivePortalType || 'self_serve_portal' : 'self';
  return `${actorId}:${userRole || ''}:${effectiveId}:${portalScope}:${contextReady ? 'ready' : 'pending'}`;
};

const canRunPortalFetch = (fetchKey, { force = false } = {}) => {
  if (force) return true;
  if (portalFetchTracker.inFlight) return false;
  if (portalFetchTracker.fetchKey !== fetchKey) return true;
  return Date.now() - portalFetchTracker.lastFetchAt > PORTAL_FETCH_COOLDOWN_MS;
};

export const PortalProvider = ({ children }) => {
  const { user, token, isAuthenticated, sessionReady, awaitingAccountSelection, handleScopedAuthFailure } = useAuth();
  const { isInSupportMode, supportContext, contextReady } = useSupportContext();
  const [portalData, setPortalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasPortalDataRef = useRef(false);

  const userId = user?.user_id;
  const userRole = resolveSessionRole(user) || user?.role;
  const effectiveUserId = supportContext?.effective_user_id;
  const effectivePortalType = supportContext?.effective_portal_type;
  const effectiveRole = supportContext?.effective_role;

  useEffect(() => {
    hasPortalDataRef.current = portalData != null;
  }, [portalData]);

  const fetchPortalAccess = useCallback(async ({ force = false } = {}) => {
    if (process.env.NODE_ENV === 'development') {
      console.count('[PortalContext] fetchPortalAccess called');
    }

    if (awaitingAccountSelection || user?.requires_account_selection || user?.pending_account_selection) {
      setPortalData(null);
      setLoading(false);
      return;
    }

    if (!isAuthenticated || !token || !sessionReady) {
      setPortalData(null);
      setLoading(Boolean(token) && !sessionReady);
      return;
    }

    // Self-repair scoped session: never call staff portal APIs or invent staff_portal.
    if (isScopedSelfRepairSession(user) && !isInSupportMode) {
      setPortalData({
        user_id: userId,
        role: userRole || 'self_repair_customer',
        portal_type: PORTAL_TYPES.SELF_SERVE,
        agency_id: null,
        is_staff: false,
        is_client: true,
        is_self_serve: true,
        is_managed_client: false,
        capabilities: {
          view_all_cases: false,
          select_disputes: true,
          create_disputes: true,
          create_document_requests: false,
          upload_documents: true,
          send_messages: true,
          access_staff_queues: false,
        },
      });
      setLoading(false);
      setError(null);
      return;
    }

    if (isDevAuthToken(token)) {
      setPortalData({
        user_id: userId,
        role: userRole,
        portal_type: 'platform_owner',
        is_staff: true,
        is_client: false,
        is_self_serve: false,
        is_managed_client: false,
        is_platform_owner: true,
        capabilities: {
          view_all_cases: true,
          select_disputes: true,
          create_disputes: true,
          create_document_requests: true,
          upload_documents: true,
          send_messages: true,
          access_staff_queues: true,
        },
      });
      setLoading(false);
      setError(null);
      return;
    }

    const fetchKey = buildPortalFetchKey({
      userId,
      userRole,
      isInSupportMode,
      effectiveUserId,
      effectivePortalType,
      contextReady,
    });

    if (!canRunPortalFetch(fetchKey, { force })) {
      return;
    }

    const isPlatformConsoleRole =
      userRole === 'platform_owner' || userRole === 'super_admin';

    if (isPlatformConsoleRole && !contextReady) {
      if (!hasPortalDataRef.current) {
        setPortalData({
          user_id: userId,
          role: userRole,
          portal_type: 'platform_owner',
          is_staff: true,
          is_client: false,
          is_self_serve: false,
          is_managed_client: false,
          is_platform_owner: true,
          capabilities: {
            view_all_cases: true,
            select_disputes: true,
            create_disputes: true,
            create_document_requests: true,
            upload_documents: true,
            send_messages: true,
            access_staff_queues: true,
          },
        });
        setLoading(false);
      }
      return;
    }

    if (!canCallAgencyStaffApis(user) && !isPlatformConsoleRole && !isInSupportMode) {
      // Identity without agency/platform scope — do not probe staff endpoints.
      const portalHint = resolveSessionPortal(user);
      setPortalData({
        user_id: userId,
        role: userRole,
        portal_type:
          portalHint === 'client' || portalHint === 'managed_client'
            ? PORTAL_TYPES.MANAGED_CLIENT
            : PORTAL_TYPES.SELF_SERVE,
        agency_id: null,
        is_staff: false,
        is_client: true,
        is_self_serve: true,
        is_managed_client: portalHint === 'client' || portalHint === 'managed_client',
        capabilities: {
          view_all_cases: false,
          select_disputes: true,
          create_disputes: true,
          create_document_requests: false,
          upload_documents: true,
          send_messages: true,
          access_staff_queues: false,
        },
      });
      setLoading(false);
      setError(null);
      return;
    }

    if (!hasPortalDataRef.current) {
      setLoading(true);
    }

    portalFetchTracker.inFlight = true;
    portalFetchTracker.fetchKey = fetchKey;

    try {
      const response = await axios.get(`${API}/portal/access/check`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });

      setPortalData(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch portal access:', err);
      const status = err?.response?.status;

      if (status === 403) {
        clearAccountScopedClientState();
        setPortalData(null);
        setError(err?.response?.data?.detail || 'Account context mismatch');
        if (typeof handleScopedAuthFailure === 'function') {
          handleScopedAuthFailure(err);
        } else {
          const path = homePathForScopedSession(user) || '/choose-account';
          if (typeof window !== 'undefined') {
            window.location.assign(path === '/unauthorized' ? '/choose-account' : path);
          }
        }
      } else if (isInSupportMode && effectivePortalType) {
        const effectivePortal = effectivePortalType;
        setPortalData({
          user_id: effectiveUserId,
          role: effectiveRole || 'self_repair_customer',
          portal_type: effectivePortal,
          is_staff: effectivePortal === PORTAL_TYPES.STAFF,
          is_client: effectivePortal !== PORTAL_TYPES.STAFF,
          is_self_serve: effectivePortal === PORTAL_TYPES.SELF_SERVE,
          is_managed_client: effectivePortal === PORTAL_TYPES.MANAGED_CLIENT,
          is_support_mode: true,
          capabilities: {
            view_all_cases: false,
            select_disputes: effectivePortal !== PORTAL_TYPES.MANAGED_CLIENT,
            create_disputes: effectivePortal !== PORTAL_TYPES.MANAGED_CLIENT,
            create_document_requests: false,
            upload_documents: true,
            send_messages: true,
            access_staff_queues: false,
          },
        });
      } else {
        // Fail closed: do NOT invent staff_portal from a cached users.role.
        setPortalData({
          user_id: userId,
          role: userRole || 'self_repair_customer',
          portal_type: PORTAL_TYPES.SELF_SERVE,
          agency_id: null,
          is_staff: false,
          is_client: true,
          is_self_serve: true,
          is_managed_client: false,
          capabilities: {
            view_all_cases: false,
            select_disputes: true,
            create_disputes: true,
            create_document_requests: false,
            upload_documents: true,
            send_messages: true,
            access_staff_queues: false,
          },
        });
      }
      setError(err.message);
    } finally {
      portalFetchTracker.inFlight = false;
      portalFetchTracker.lastFetchAt = Date.now();
      setLoading(false);
    }
  }, [
    isAuthenticated,
    sessionReady,
    awaitingAccountSelection,
    token,
    user,
    userId,
    userRole,
    isInSupportMode,
    effectiveUserId,
    effectivePortalType,
    effectiveRole,
    contextReady,
    handleScopedAuthFailure,
  ]);

  useEffect(() => {
    fetchPortalAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scalar identity only; avoid refetch on callback churn
  }, [
    isAuthenticated,
    sessionReady,
    awaitingAccountSelection,
    token,
    userId,
    userRole,
    isInSupportMode,
    effectiveUserId,
    effectivePortalType,
    contextReady,
  ]);

  // Derived values
  const portalType = portalData?.portal_type || PORTAL_TYPES.SELF_SERVE;
  const isStaff = portalData?.is_staff || false;
  const isClient = portalData?.is_client || true;
  const isSelfServe = portalData?.is_self_serve || false;
  const isManagedClient = portalData?.is_managed_client || false;
  const capabilities = portalData?.capabilities || {};

  // Check specific capability
  const hasCapability = useCallback((capability) => {
    return capabilities[capability] || false;
  }, [capabilities]);

  // Get portal-specific dashboard path
  const getDashboardPath = useCallback(() => {
    switch (portalType) {
      case PORTAL_TYPES.STAFF:
        return '/staff/dashboard';
      case PORTAL_TYPES.SELF_SERVE:
        return '/dashboard';
      case PORTAL_TYPES.MANAGED_CLIENT:
        return '/client/dashboard';
      default:
        return '/dashboard';
    }
  }, [portalType]);

  const routeLoading = loading && !portalData;

  return (
    <PortalContext.Provider value={{
      // Raw data
      portalData,
      loading: routeLoading,
      refreshing: loading && Boolean(portalData),
      error,
      
      // Portal type
      portalType,
      isStaffPortal: portalType === PORTAL_TYPES.STAFF,
      isSelfServePortal: portalType === PORTAL_TYPES.SELF_SERVE,
      isManagedClientPortal: portalType === PORTAL_TYPES.MANAGED_CLIENT,
      
      // Role-based flags
      isStaff,
      isClient,
      isSelfServe,
      isManagedClient,
      
      // Capabilities
      capabilities,
      hasCapability,
      
      // Helpers
      getDashboardPath,
      refreshPortalData: fetchPortalAccess,
      
      // Constants
      PORTAL_TYPES,
    }}>
      {children}
    </PortalContext.Provider>
  );
};

export default PortalContext;
